import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageMeta } from '../../core/usePageMeta';
import { ROUTES } from '../../app/routes';
import { useAppSelector } from '../../app/store';
import { ApplyDialog } from '../hub/ApplyDialog';
import { isNotFound, publicApi, type JobCard, type JobDetail, type Paged } from './publicApi';
import { companyPath, openCompany, EmptyState, employmentLabel, ErrorState, formatMoney, JobCardView, LoadingGrid, PublicLayout, usePublicLanguage } from './components';

/** `/jobs` — open positions of every listed organization (Hub design 06). */
export function JobsPage() {
  const { t } = useTranslation();
  usePageMeta(t('hub.jobs.title', 'Jobs'));
  const lang = usePublicLanguage();
  const [params, setParams] = useSearchParams();
  const [result, setResult] = useState<Paged<JobCard> | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [q, setQ] = useState(params.get('q') ?? '');
  const [city, setCity] = useState(params.get('city') ?? '');
  const page = Math.max(1, Number(params.get('page')) || 1);

  useEffect(() => {
    let active = true;
    setResult(null);
    setError(false);
    publicApi.jobs(lang, { q: params.get('q') ?? undefined, city: params.get('city') ?? undefined, page, pageSize: 24 })
      .then((data) => { if (active) setResult(data); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [lang, params, page, attempt]);

  function submit(event: FormEvent) {
    event.preventDefault();
    const next = new URLSearchParams();
    if (q.trim()) next.set('q', q.trim());
    if (city.trim()) next.set('city', city.trim());
    setParams(next);
  }

  const pages = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;
  return <PublicLayout>
    <div className="hub-wrap hub-section">
      <div className="hub-section-head"><h1 className="hub-page-title">{t('hub.jobs.title', 'Jobs')}</h1>
        {result && <span className="hub-muted">{t('hub.jobs.count', '{{count}} open positions', { count: result.total })}</span>}</div>
      <form className="hub-search" role="search" onSubmit={submit} style={{ maxWidth: 760, marginBottom: 22 }}>
        <label className="hub-sr" htmlFor="hub-job-q">{t('hub.jobs.what', 'Job, business or role')}</label>
        <input id="hub-job-q" value={q} onChange={(event) => setQ(event.target.value)} placeholder={t('hub.jobs.what', 'Job, business or role')} />
        <label className="hub-sr" htmlFor="hub-job-city">{t('hub.search.where', 'City')}</label>
        <input id="hub-job-city" value={city} onChange={(event) => setCity(event.target.value)} placeholder={t('hub.search.where', 'City')} />
        <button type="submit">{t('hub.search.submit', 'Search')}</button>
      </form>
      {error && <ErrorState message={t('hub.jobs.loadError', 'Jobs could not be loaded.')} onRetry={() => setAttempt((value) => value + 1)} />}
      {!result && !error && <LoadingGrid label={t('hub.loading', 'Loading')} />}
      {result && result.items.length === 0 && <EmptyState>{t('hub.landing.noJobs', 'No open positions right now.')}</EmptyState>}
      {result && result.items.length > 0 && <div className="hub-grid">{result.items.map((job) => <JobCardView key={job.id} job={job} />)}</div>}
      {result && pages > 1 && <nav className="hub-pager" aria-label={t('hub.search.pages', 'Pages')}>
        <button type="button" className="hub-btn sm ghost" disabled={page <= 1} onClick={() => { const n = new URLSearchParams(params); n.set('page', String(page - 1)); setParams(n); }}>{t('hub.search.previous', 'Previous')}</button>
        <span>{t('hub.search.pageOf', 'Page {{page}} of {{pages}}', { page, pages })}</span>
        <button type="button" className="hub-btn sm ghost" disabled={page >= pages} onClick={() => { const n = new URLSearchParams(params); n.set('page', String(page + 1)); setParams(n); }}>{t('hub.search.next', 'Next')}</button>
      </nav>}
    </div>
  </PublicLayout>;
}

/** `/jobs/:jobId` — job page. Online applications arrive with the jobs wave (BE-08 / FE-06). */
export function JobPage() {
  // HUB-UI: customers apply from the public vacancy; anonymous visitors sign in first.
  const signedIn = useAppSelector(state => state.session.status === 'authenticated');
  const [applying, setApplying] = useState(false);
  const { t } = useTranslation();
  const lang = usePublicLanguage();
  const { jobId = '' } = useParams();
  const [detail, setDetail] = useState<JobDetail | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setState('loading');
    publicApi.job(lang, jobId)
      .then((data) => { if (active) { setDetail(data); setState('ready'); } })
      .catch((error: unknown) => { if (active) setState(isNotFound(error) ? 'missing' : 'error'); });
    return () => { active = false; };
  }, [lang, jobId, attempt]);

  const job = detail?.job;
  usePageMeta(job ? `${job.title} · ${job.organizationName}` : t('hub.jobs.title', 'Jobs'), { description: detail?.description });
  const salary = job && job.salaryMin != null
    ? [formatMoney(job.salaryMin, job.salaryCurrency, lang), job.salaryMax != null ? formatMoney(job.salaryMax, job.salaryCurrency, lang) : null].filter(Boolean).join(' – ')
    : null;

  return <PublicLayout>
    <div className="hub-wrap hub-section">
      <p><Link to={ROUTES.jobs}>← {t('hub.jobs.back', 'All jobs')}</Link></p>
      {state === 'loading' && <div className="hub-skeleton" role="status" aria-label={t('hub.loading', 'Loading')} style={{ minHeight: 240 }} />}
      {state === 'missing' && <EmptyState>{t('hub.jobs.notFound', 'This position is no longer open.')}</EmptyState>}
      {state === 'error' && <ErrorState message={t('hub.jobs.loadError', 'Jobs could not be loaded.')} onRetry={() => setAttempt((value) => value + 1)} />}
      {state === 'ready' && detail && job && <div className="hub-company-body" style={{ paddingTop: 0 }}>
        <article className="hub-panel">
          <h1 className="hub-page-title" style={{ marginBottom: 6 }}>{job.title}</h1>
          <p className="hub-muted" style={{ marginTop: 0 }}>
            {job.organizationSlug
              ? <Link to={companyPath(job.organizationSlug)} onClick={(event) => openCompany(event, job.organizationSlug)}>{job.organizationName}</Link>
              : job.organizationName} · {job.city}
          </p>
          <p><span className="hub-pill job">{employmentLabel(t, job.employmentType)}</span></p>
          {detail.description && <><h2>{t('hub.jobs.description', 'Description')}</h2><p className="hub-prose">{detail.description}</p></>}
          {detail.requirements && <><h2>{t('hub.jobs.requirements', 'Requirements')}</h2><p className="hub-prose">{detail.requirements}</p></>}
          {detail.benefits && <><h2>{t('hub.jobs.benefits', 'Benefits')}</h2><p className="hub-prose">{detail.benefits}</p></>}
        </article>
        <aside className="hub-panel">
          <dl>
            {job.roleName && <><dt>{t('hub.jobs.role', 'Role')}</dt><dd>{job.roleName}</dd></>}
            {salary && <><dt>{t('hub.jobs.salary', 'Salary')}</dt><dd>{salary}</dd></>}
            {detail.addressLine && <><dt>{t('hub.company.address', 'Address')}</dt><dd>{detail.addressLine}</dd></>}
            {detail.closesOn && <><dt>{t('hub.jobs.closes', 'Closes')}</dt><dd>{detail.closesOn}</dd></>}
          </dl>
          {signedIn ? <button className="hub-btn" type="button" onClick={() => setApplying(true)}>{t('hub.jobs.apply', 'Apply for this position')}</button> : <Link className="hub-btn" to={ROUTES.login} state={{ from: `/me/applications?jobId=${encodeURIComponent(jobId)}` }}>{t('hub.jobs.signInApply', 'Sign in to apply')}</Link>}
          {job.organizationSlug && <Link className="hub-btn ghost" to={companyPath(job.organizationSlug)} onClick={(event) => openCompany(event, job.organizationSlug)}>{t('hub.jobs.viewCompany', 'View business')}</Link>}
        </aside>
      </div>}
      {applying && <ApplyDialog jobId={jobId} close={() => setApplying(false)} />}
    </div>
  </PublicLayout>;
}

/** `/for-business` — why list on Architech and where to start. */
export function ForBusinessPage() {
  const { t } = useTranslation();
  usePageMeta(t('hub.nav.forBusiness', 'For business'), { description: t('hub.business.title', 'Run your business on Architech') });
  const products = [
    ['Restaurant', t('hub.business.restaurant', 'Tables, menu, orders, kitchen and fiscal invoices.')],
    ['Cosmetics', t('hub.business.cosmetics', 'Online booking, staff calendar and a shop for salons and spas.')],
    ['Hotel', t('hub.business.hotel', 'Rooms, reservations and guests.')],
    ['Pharmacy', t('hub.business.pharmacy', 'Stock, sales and prescriptions.')],
    ['Auto service', t('hub.business.auto', 'Work orders, parts and customers.')],
  ];
  return <PublicLayout>
    <section className="hub-hero"><div className="hub-wrap">
      <h1>{t('hub.business.title', 'Run your business on Architech')}</h1>
      <p>{t('hub.business.subtitle', 'One account and one company profile — listed in the Architech directory for every customer to find.')}</p>
      <Link className="hub-btn" to={ROUTES.register}>{t('hub.business.cta', 'Create an account')}</Link>
    </div></section>
    <div className="hub-wrap hub-section">
      <div className="hub-grid">
        {products.map(([name, text]) => <article key={name} className="hub-card"><div className="pad"><h3>{name}</h3><span className="hub-muted">{text}</span></div></article>)}
      </div>
      <div className="hub-banner">
        <div>
          <h2>{t('hub.business.alreadyTitle', 'Already a customer?')}</h2>
          <p>{t('hub.business.alreadyText', 'Sign in and complete your company profile to appear in the directory.')}</p>
        </div>
        <Link className="hub-btn" to={ROUTES.login}>{t('hub.nav.signIn', 'Sign in')}</Link>
      </div>
    </div>
  </PublicLayout>;
}
